import { fetchAllPages } from '@frontend/api-utils';
import { useAuthorization } from '@frontend/authorization';
import { BlobClient } from '@frontend/blob/api';
import { ColliClient } from '@frontend/colli/api';
import { ColliStateBadge } from '@frontend/colli/common';
import { Colli } from '@frontend/colli/types';
import {
    DateFormat,
    EntitySharing,
    EntityType,
    ResolvedEntity,
    calculateTimeDifferenceBetweenDates,
    formatDateTimeWithLocale,
    sharedFromAccountName
} from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton, Spinner, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { DeceasedClient } from '@frontend/deceased/api';
import { DeceasedStatusBadge } from '@frontend/deceased/badge';
import { IDeceased } from '@frontend/deceased/types';
import { EventLevel, EventType } from '@frontend/events/types';
import { Application } from '@frontend/functionality/types';
import { LabelClient } from '@frontend/label/api';
import { Label } from '@frontend/label/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { PinCodeType } from '@frontend/pin-code/types';
import {
    TransactionAttachmentClient,
    TransactionLendingClient,
    TransactionNivalizExtendedClient,
    TransactionPudoExtendedClient,
    TransactionVendingClient
} from '@frontend/transaction/api';
import {
    HistoryEventDetails,
    ShippingNotesDisplay,
    TransactionHistory,
    TransactionStateBadge,
    getHistoryEventTitleClassName,
    mapEventDataToDisplay
} from '@frontend/transaction/common';
import {
    ExtendedLendingTransaction,
    ExtendedNivalizTransaction,
    ExtendedPudoTransaction,
    ExtendedVendingTransaction,
    TransactionAttachmentType
} from '@frontend/transaction/types';
import React, { useEffect, useState } from 'react';
import { LuFile } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { TransactionStatusCell } from '../../applications/pudo/transactions_collis/transaction-status-cell/transaction-status-cell.component';
import { EntitySharingSection } from '../../components/entity-sharing-section/entity-sharing-section.component';
import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformEventReferenceBadges } from '../../modal/platform-event-reference-badges/platform-event-reference-badges.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { PlatformResolvedEntityBadge } from '../../modal/platform-object-badge/platform-resolved-entity-badge.component';
import { triggerRefetch } from '../../redux/detail-modal-slice';
import { useAppDispatch } from '../../redux/store';
import { CancelTransactionActionButton, getCancelTransactionCompactActionItem } from './cancel-transaction-action/cancel-transaction-action.component';
import { ChangeTransactionStateModal } from './change-transaction-state-modal/change-transaction-state-modal.component';
import {
    DelegateTransactionActionButton,
    canDelegateTransaction,
    getDelegateTransactionCompactActionItem
} from './delegate-transaction-action/delegate-transaction-action.component';
import { PrintModal } from './print-modal/print-modal';
import { ProofOfIdentityCard } from './proof-of-identity-card/proof-of-identity-card.component';
import useTransactionDetailModal, { TransactionDetail, TransactionDetailAttachment } from './transaction-detail-modal.controller';
import { buildSlotParentIds } from './transaction-detail-slot-parents';

export function useTransactionCollis(transaction: TransactionDetail | null): Colli[] | null {
    const [collis, setCollis] = useState<Colli[] | null>(null);
    useEffect(() => {
        if (!transaction || !('pin_codes' in transaction)) {
            setCollis(null);
            return;
        }
        let cancelled = false;
        ColliClient.fetchCollis({ transaction_id: transaction.id, account_id: transaction.account_id }).then((res) => {
            if (!cancelled) setCollis(res.results);
        });
        return () => {
            cancelled = true;
        };
    }, [transaction]);
    return collis;
}

export function useTransactionAttachments(transaction: TransactionDetail | null): TransactionDetailAttachment[] {
    const [attachments, setAttachments] = useState<TransactionDetailAttachment[]>([]);
    useEffect(() => {
        if (!transaction) {
            setAttachments([]);
            return;
        }
        let cancelled = false;
        const createdUrls: string[] = [];
        const loadAttachments = async () => {
            const records = await fetchAllPages((queryParams) => TransactionAttachmentClient.fetchTransactionAttachments(queryParams), {
                transaction_id: transaction.id
            });
            const loadedAttachments = await Promise.all(
                records.map(async (record): Promise<TransactionDetailAttachment | null> => {
                    try {
                        const { blob, filename } = await BlobClient.fetchBlobWithFilename(transaction.account_id, EntityType.TRANSACTION_ATTACHMENT, record.id);
                        if (cancelled) return null;
                        const url = URL.createObjectURL(blob);
                        createdUrls.push(url);
                        return { id: record.id, name: filename || record.id, type: blob.type, attachmentType: record.type, url };
                    } catch {
                        return null;
                    }
                })
            );
            if (!cancelled) {
                setAttachments(loadedAttachments.filter((attachment): attachment is TransactionDetailAttachment => attachment !== null));
            }
        };
        void loadAttachments().catch(() => {
            if (!cancelled) setAttachments([]);
        });
        return () => {
            cancelled = true;
            createdUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [transaction]);
    return attachments;
}

export type PinCodeValidityStatus = 'never' | 'expired' | 'active';

/** Resolve PIN-code card expiry label: no timestamp, past/equal now, or still valid. */
export const getPinCodeValidityStatus = (validTimestamp: string | null | undefined, nowMs: number = Date.now()): PinCodeValidityStatus => {
    if (!validTimestamp) return 'never';
    const validMs = Date.parse(validTimestamp);
    if (Number.isNaN(validMs) || validMs <= nowMs) return 'expired';
    return 'active';
};

/** PuDo and Nivaliz share pin_codes / collis / packages shape (not vending/lending). */
export const isPudoOrNivalizTransaction = (transaction: TransactionDetail): transaction is ExtendedPudoTransaction | ExtendedNivalizTransaction =>
    'pin_codes' in transaction && !('product' in transaction);

export const isPudoTransaction = (transaction: TransactionDetail): transaction is ExtendedPudoTransaction =>
    isPudoOrNivalizTransaction(transaction) && transaction.application !== Application.NIVALIZ;

export const isNivalizTransaction = (transaction: TransactionDetail): transaction is ExtendedNivalizTransaction =>
    isPudoOrNivalizTransaction(transaction) && transaction.application === Application.NIVALIZ;

export const isProductTransaction = (transaction: TransactionDetail): transaction is ExtendedVendingTransaction | ExtendedLendingTransaction =>
    'product' in transaction;
export const isLaundryTransaction = (transaction: TransactionDetail | LaundryTransaction): transaction is LaundryTransaction =>
    'contents' in transaction && Array.isArray((transaction as LaundryTransaction).contents);

export type TransactionDetailsObject = TransactionDetail | LaundryTransaction;

export const getTransactionDetailOrder = (transaction: TransactionDetailsObject) => {
    if (isLaundryTransaction(transaction)) {
        return ['tracking_number', 'spot', 'slot', 'creation_timestamp', 'update_timestamp', 'state'];
    }
    if (isProductTransaction(transaction)) {
        return [
            'tracking_number',
            'user',
            'product',
            'sku',
            'ean',
            'quantity',
            'spot',
            'cost_center',
            'initiated_by',
            'creation_timestamp',
            'update_timestamp',
            'state'
        ];
    }

    return ['tracking_number', 'user_id', 'dropped_off_by', 'picked_up_by', 'creation_timestamp', 'update_timestamp', 'state', 'supplier'];
};

export const fetchTransactionDetail = (application: Application, accountId: string, transactionId: string): Promise<TransactionDetail> => {
    switch (application) {
        case Application.VENDING:
            return TransactionVendingClient.fetchVendingTransaction(accountId, transactionId);
        case Application.LENDING:
            return TransactionLendingClient.fetchLendingTransaction(accountId, transactionId);
        case Application.NIVALIZ:
            return TransactionNivalizExtendedClient.fetchExtendedNivalizTransaction(accountId, transactionId);
        default:
            return TransactionPudoExtendedClient.fetchPudoExtendedTransaction(accountId, transactionId);
    }
};

export const formatColliType = (type: string | null) => {
    if (!type) return '/';
    return (type.charAt(0).toUpperCase() + type.slice(1)).replace(/_/g, ' ');
};

export const getTransactionAttachmentTypeLabel = (type: TransactionAttachmentType | null) => {
    switch (type) {
        case TransactionAttachmentType.SHIPPING_NOTES:
            return CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES;
        default:
            return null;
    }
};

export const TransactionAttachmentTypeBadge = ({ type }: { type: TransactionAttachmentType | null }) => {
    const label = getTransactionAttachmentTypeLabel(type);
    if (!label) return null;

    return <span className='bg-background-surface/90 text-foreground-default rounded px-2 py-1 text-xs font-medium shadow-sm'>{label}</span>;
};

export const getTransactionDetailKeyOverwrite = () =>
    new Map([
        [
            'user_id',
            <FormattedMessage
                key='Creator'
                id='Creator'
                description='Creator of a transaction (based on user_id)'
                defaultMessage='Creator'
            />
        ],
        ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
        ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
        ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
        ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
        ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
        ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY],
        ['cost_center', CommonMessage.OBJECTS.COST_CENTER.DEFAULT],
        ['supplier', CommonMessage.OBJECTS.SUPPLIER.DEFAULT]
    ]);

export const getTransactionDetailValueOverwrite = (collis: Colli[] | null) =>
    new Map([
        [
            'state',
            (obj: TransactionDetailsObject) =>
                !isLaundryTransaction(obj) && isPudoOrNivalizTransaction(obj) ? (
                    <TransactionStatusCell
                        transaction={obj}
                        collis={collis ?? obj.collis}
                    />
                ) : (
                    <TransactionStateBadge state={obj.state} />
                )
        ]
    ]);

export const TransactionDetailsCard = ({ obj, collis }: { obj: TransactionDetailsObject; collis: Colli[] | null }) => (
    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={obj}
            cols={3}
            order={getTransactionDetailOrder(obj)}
            keyOverwrite={getTransactionDetailKeyOverwrite()}
            valueOverwrite={getTransactionDetailValueOverwrite(collis)}
        />
    </div>
);

const TransactionPartyBadges = ({ entities, parentShared }: { entities: ResolvedEntity[] | null | undefined; parentShared?: EntitySharing | null }) => {
    if (!entities?.length) {
        return null;
    }
    const [first, ...rest] = entities;
    return (
        <div className='flex w-full min-w-0 flex-wrap items-center gap-1'>
            <PlatformResolvedEntityBadge
                entity={first}
                entityType={EntityType.USER}
                parentShared={parentShared}
            />
            {rest.length > 0 && (
                <MultiEntityButton
                    entities={rest}
                    render={(entity) => (
                        <PlatformResolvedEntityBadge
                            entity={entity}
                            entityType={EntityType.USER}
                            parentShared={parentShared}
                        />
                    )}
                />
            )}
        </div>
    );
};

export const LaundryTransactionGeneralSection = ({ obj }: { obj: LaundryTransaction }) => (
    <div className='inline-flex w-full items-start justify-start gap-6 self-stretch'>
        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.SENDER}</div>
            <TransactionPartyBadges entities={obj.senders} />
        </div>
        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVER}</div>
            <TransactionPartyBadges entities={obj.receivers} />
        </div>
    </div>
);

export const TransactionHeaderActions = ({
    obj,
    viewProps,
    dispatch
}: {
    obj: TransactionDetail;
    viewProps: ReturnType<typeof useTransactionDetailModal>;
    dispatch: ReturnType<typeof useAppDispatch>;
}) => {
    const { permissions } = useAuthorization();
    const openChangeTransactionStateModal = () => {
        viewProps.openModal(
            `change-transaction-state-modal-${obj.id}`,
            () => (
                <ChangeTransactionStateModal
                    transactions={[obj]}
                    onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
                />
            ),
            {},
            { className: 'w-[390px]' }
        );
    };
    return (
        <>
            <DefaultPrimaryButton
                text={CommonMessage.BUTTONS.CHANGE_STATUS}
                onClick={openChangeTransactionStateModal}
                size='sm'
            />
            {isPudoOrNivalizTransaction(obj) && canDelegateTransaction(permissions, obj) && (
                <DelegateTransactionActionButton
                    transaction={obj}
                    onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
                />
            )}
            <CancelTransactionActionButton
                transaction={obj}
                canRender={viewProps.canRenderCancelButton}
                onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
            />
        </>
    );
};

export const getTransactionCompactHeaderActionItems = ({
    obj,
    viewProps,
    dispatch,
    permissions
}: {
    obj: TransactionDetail;
    viewProps: ReturnType<typeof useTransactionDetailModal>;
    dispatch: ReturnType<typeof useAppDispatch>;
    permissions: ReturnType<typeof useAuthorization>['permissions'];
}) => {
    const actionItems: Array<{ label: React.ReactNode; onClick: () => void }> = [
        {
            label: CommonMessage.BUTTONS.CHANGE_STATUS,
            onClick: () => {
                viewProps.openModal(
                    `change-transaction-state-modal-${obj.id}`,
                    () => (
                        <ChangeTransactionStateModal
                            transactions={[obj]}
                            onSubmitCallback={() => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))}
                        />
                    ),
                    {},
                    { className: 'w-[390px]' }
                );
            }
        }
    ];
    if (isPudoOrNivalizTransaction(obj) && canDelegateTransaction(permissions, obj)) {
        actionItems.push(
            getDelegateTransactionCompactActionItem({
                transaction: obj,
                openModal: viewProps.openModal,
                onSubmitCallback: () => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))
            })
        );
    }
    const cancelAction = getCancelTransactionCompactActionItem({
        transaction: obj,
        canRender: viewProps.canRenderCancelButton,
        openModal: viewProps.openModal,
        onSubmitCallback: () => dispatch(triggerRefetch({ entityType: EntityType.TRANSACTION, entityId: obj.id }))
    });
    if (cancelAction) actionItems.push(cancelAction);
    return actionItems;
};

function formatOptionalDeceasedDate(value: string | null | undefined, locale: string, format: DateFormat): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatDateTimeWithLocale(date, locale, format);
}

export const TransactionDeceasedCard = ({ transaction }: { transaction: ExtendedNivalizTransaction }) => {
    const { locale } = useLocale();
    const deceasedId = transaction.deceased?.entity_id ?? transaction.deceased_id;
    const [deceased, setDeceased] = useState<IDeceased | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!transaction.account_id || !deceasedId) {
            setDeceased(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        DeceasedClient.fetchDeceased(transaction.account_id, deceasedId)
            .then((result) => {
                if (!cancelled) setDeceased(result ?? null);
            })
            .catch(() => {
                if (!cancelled) setDeceased(null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [transaction.account_id, deceasedId]);

    if (!deceasedId) return null;

    const dateOfDeathLabel = formatOptionalDeceasedDate(deceased?.date_of_death, locale ?? 'en', DateFormat.DD_MMMM_YYYY);
    const cremationLabel = formatOptionalDeceasedDate(deceased?.cremation_timestamp, locale ?? 'en', DateFormat.DD_MMMM_HH_MM);
    const elysLabel = deceased?.elys_number?.trim() || null;

    return (
        <div
            data-testid='transaction-deceased-card'
            className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-4 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.DECEASED.DEFAULT}</div>
            {isLoading ? (
                <div className='flex w-full items-center justify-center py-2'>
                    <Spinner />
                </div>
            ) : (
                <div className='inline-flex w-full flex-col items-start gap-3'>
                    <PlatformObjectBadge
                        id={deceasedId}
                        entityType={EntityType.DECEASED}
                        display={deceased?.name ?? transaction.deceased?.name}
                        foreign={transaction.deceased?.foreign}
                        accountId={accountIdFromResolvedEntity(transaction.deceased)}
                        accountName={sharedFromAccountName(transaction.shared)}
                    />
                    {deceased?.state && (
                        <div className='inline-flex w-full items-center justify-between gap-3'>
                            <div className='text-brand-muted text-sm font-medium'>{CommonMessage.OBJECTS.COMMON_FIELDS.STATUS}</div>
                            <DeceasedStatusBadge status={deceased.state} />
                        </div>
                    )}
                    {dateOfDeathLabel && (
                        <div className='inline-flex w-full items-center justify-between gap-3'>
                            <div className='text-brand-muted text-sm font-medium'>{CommonMessage.OBJECTS.DECEASED.DATE_OF_DEATH}</div>
                            <div className='text-foreground-default text-sm font-medium'>{dateOfDeathLabel}</div>
                        </div>
                    )}
                    {elysLabel && (
                        <div className='inline-flex w-full items-center justify-between gap-3'>
                            <div className='text-brand-muted text-sm font-medium'>{CommonMessage.OBJECTS.DECEASED.ELYS_NUMBER}</div>
                            <div className='text-foreground-default text-sm font-medium'>{elysLabel}</div>
                        </div>
                    )}
                    {cremationLabel && (
                        <div className='inline-flex w-full items-center justify-between gap-3'>
                            <div className='text-brand-muted text-sm font-medium'>{CommonMessage.OBJECTS.DECEASED.CREMATION}</div>
                            <div className='text-foreground-default text-sm font-medium'>{cremationLabel}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export const TransactionGeneralSection = ({ obj }: { obj: TransactionDetail }) => {
    const viewPropsCollis = useTransactionCollis(obj);
    const viewProps = { collis: viewPropsCollis };
    const shared = 'shared' in obj ? obj.shared : undefined;
    return (
        <>
            {isNivalizTransaction(obj) && <TransactionDeceasedCard transaction={obj} />}
            <EntitySharingSection shared={shared} />
            {isPudoOrNivalizTransaction(obj) && (
                <>
                    <div className='inline-flex w-full items-start justify-start gap-6 self-stretch'>
                        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.SENDER}</div>
                            <TransactionPartyBadges
                                entities={obj.senders}
                                parentShared={obj.shared}
                            />
                        </div>
                        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.RECEIVER}</div>
                            <TransactionPartyBadges
                                entities={obj.receivers}
                                parentShared={obj.shared}
                            />
                        </div>
                    </div>
                    <div className='inline-flex w-full items-start justify-start gap-6 self-stretch'>
                        {viewProps.collis && viewProps.collis.length > 0 && (
                            <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-3 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                                <div className='text-foreground-default justify-start text-xl font-semibold'>
                                    <FormattedMessage
                                        id='transaction.detail-modal.current-location'
                                        defaultMessage='Current location'
                                    />
                                </div>
                                <div className='inline-flex flex-col gap-2'>
                                    {Array.from(
                                        viewProps.collis.reduce((map, colli) => {
                                            if (!colli.current_spot_id) return map;
                                            if (!map.has(colli.current_spot_id)) {
                                                map.set(colli.current_spot_id, new Map<string, string | undefined>());
                                            }
                                            if (colli.current_slot_id) {
                                                map.get(colli.current_spot_id)!.set(colli.current_slot_id, colli.current_module_id ?? undefined);
                                            }
                                            return map;
                                        }, new Map<string, Map<string, string | undefined>>())
                                    ).map(([spotId, slotsById]) => {
                                        const slotEntries = Array.from(slotsById.entries());
                                        const visible = slotEntries.slice(0, 3);
                                        const overflow = slotEntries.slice(3);
                                        return (
                                            <div
                                                key={spotId}
                                                className='inline-flex flex-wrap items-center gap-2'>
                                                <PlatformObjectBadge
                                                    id={spotId}
                                                    entityType={EntityType.SPOT}
                                                />
                                                {visible.map(([slotId, moduleId]) => (
                                                    <PlatformObjectBadge
                                                        key={slotId}
                                                        id={slotId}
                                                        entityType={EntityType.SLOT}
                                                        parentIds={buildSlotParentIds({ spotId, moduleId })}
                                                    />
                                                ))}
                                                {overflow.length > 0 && (
                                                    <div className='group relative'>
                                                        <span className='text-brand-muted cursor-default text-sm font-medium select-none'>
                                                            +{overflow.length}
                                                        </span>
                                                        <div className='bg-background-surface outline-border-default pointer-events-none absolute bottom-full left-0 z-50 mb-1 flex flex-wrap gap-1 rounded-xl p-2 opacity-0 shadow-lg outline-1 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100'>
                                                            {overflow.map(([slotId, moduleId]) => (
                                                                <PlatformObjectBadge
                                                                    key={slotId}
                                                                    id={slotId}
                                                                    entityType={EntityType.SLOT}
                                                                    parentIds={buildSlotParentIds({ spotId, moduleId })}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className='bg-background-surface outline-border-default inline-flex flex-1 flex-col items-start justify-start gap-3 rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                            <div className='text-foreground-default justify-start text-xl font-semibold'>
                                <FormattedMessage
                                    id='transaction.detail-modal.destination'
                                    defaultMessage='Destination'
                                />
                            </div>
                            <div className='inline-flex flex-wrap items-center gap-2'>
                                {obj.spot ? (
                                    <PlatformResolvedEntityBadge
                                        entity={obj.spot}
                                        entityType={EntityType.SPOT}
                                        parentShared={obj.shared}
                                    />
                                ) : (
                                    <PlatformObjectBadge
                                        id={obj.spot_id ?? undefined}
                                        entityType={EntityType.SPOT}
                                    />
                                )}
                                {obj.slot_id &&
                                    (obj.slot ? (
                                        <PlatformResolvedEntityBadge
                                            entity={obj.slot}
                                            entityType={EntityType.SLOT}
                                            parentIds={buildSlotParentIds({ spotId: obj.spot_id, moduleId: obj.module_id })}
                                            parentShared={obj.shared}
                                        />
                                    ) : (
                                        <PlatformObjectBadge
                                            id={obj.slot_id}
                                            entityType={EntityType.SLOT}
                                            parentIds={buildSlotParentIds({ spotId: obj.spot_id, moduleId: obj.module_id })}
                                        />
                                    ))}
                            </div>
                        </div>
                    </div>
                    <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.PIN_CODE.PLURAL}</div>
                        {obj && obj.pin_codes && obj.pin_codes.length > 0 ? (
                            <div className='inline-flex flex-wrap items-start justify-start gap-3'>
                                {obj.pin_codes.map((pinCode) => {
                                    const validityStatus = getPinCodeValidityStatus(pinCode.valid_timestamp);
                                    return (
                                        <div
                                            key={pinCode.id}
                                            className='outline-border-default inline-flex w-48 flex-col items-start justify-start gap-3 rounded-md p-3 outline-1 outline-offset-[-1px]'>
                                            <div className='inline-flex items-center justify-between self-stretch'>
                                                <div className='text-foreground-default justify-start text-base font-medium'>
                                                    {pinCode.type === PinCodeType.PICK_UP && CommonMessage.BUTTONS.PICK_UP}
                                                    {pinCode.type === PinCodeType.DROP_OFF && CommonMessage.BUTTONS.DROP_OFF}
                                                    {pinCode.type !== PinCodeType.MASTER && ' '}
                                                    {CommonMessage.OBJECTS.PIN_CODE.CODE}
                                                </div>
                                                <div
                                                    data-variant='Default'
                                                    className='bg-border-default flex h-6 items-center justify-center gap-2.5 rounded px-1'>
                                                    <div className='text-foreground-default justify-start text-sm font-medium'>{pinCode.code}</div>
                                                </div>
                                            </div>
                                            <div className='text-brand-muted justify-start text-sm font-medium'>
                                                {validityStatus === 'never' && <div>{CommonMessage.OBJECTS.PIN_CODE.NEVER_EXPIRES}</div>}
                                                {validityStatus === 'expired' && <div>{CommonMessage.OBJECTS.PIN_CODE.EXPIRED}</div>}
                                                {validityStatus === 'active' && pinCode.valid_timestamp && (
                                                    <div>
                                                        {CommonMessage.OBJECTS.PIN_CODE.EXPIRES_IN(
                                                            calculateTimeDifferenceBetweenDates(new Date(Date.now()).toISOString(), pinCode.valid_timestamp)
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className='text-foreground-default justify-start text-sm font-normal'>{CommonMessage.OBJECTS.PIN_CODE.NO_PIN_CODES}</div>
                        )}
                    </div>
                    <ProofOfIdentityCard
                        transaction={obj}
                        collis={viewProps.collis}
                    />
                </>
            )}
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES}</div>
                {obj.shipping_notes && (
                    <ShippingNotesDisplay
                        value={obj.shipping_notes}
                        className='text-foreground-default justify-start text-sm font-normal'
                    />
                )}
            </div>
            {obj.purchase_order && (
                <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                    <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER}</div>
                    <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                        <div className='inline-flex items-start justify-start gap-3 self-stretch'>
                            <div className='inline-flex h-12 flex-1 flex-col items-start justify-start gap-[3px] self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>
                                    <FormattedMessage
                                        id='transaction.detail-modal.purchase-order-number'
                                        defaultMessage='PO number'
                                    />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{obj.purchase_order}</div>
                            </div>
                            <div className='inline-flex h-12 flex-1 flex-col items-start justify-start gap-[3px] self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>
                                    <FormattedMessage
                                        id='transaction.detail-modal.delivery-location'
                                        defaultMessage='Delivery location'
                                    />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-medium'>{obj.shipping_address?.name}</div>
                            </div>
                        </div>
                        <div className='inline-flex items-start justify-start gap-3 self-stretch'>
                            <div className='inline-flex h-12 flex-1 flex-col items-start justify-start gap-[3px] self-stretch'>
                                <div className='text-brand-muted justify-start text-base font-medium'>
                                    <FormattedMessage
                                        id='transaction.detail-modal.delivery-address'
                                        defaultMessage='Delivery address'
                                    />
                                </div>
                                <div className='text-foreground-default justify-start text-base font-medium'>
                                    <div className='flex gap-1'>
                                        {obj.shipping_address?.street && <span>{obj.shipping_address?.street}</span>}
                                        {obj.shipping_address?.number && <span>{obj.shipping_address?.number},</span>}
                                        {obj.shipping_address?.zip_code && <span>{obj.shipping_address?.zip_code}</span>}
                                        {obj.shipping_address?.city && <span>{obj.shipping_address?.city}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const TransactionParcelsSection = ({
    obj,
    viewProps,
    dispatch
}: {
    obj: TransactionDetail;
    viewProps: ReturnType<typeof useTransactionDetailModal>;
    dispatch: ReturnType<typeof useAppDispatch>;
}) => (
    <div className='inline-flex w-full flex-col items-start justify-start gap-6 self-stretch'>
        <ReactiveObjectList<Colli>
            key={'parcels-list'}
            fetch={(args) => ColliClient.fetchCollis({ ...args, transaction_id: obj.id, account_id: obj.account_id })}
            dispatch={dispatch}
            type={EntityType.COLLI}
            eventListener={EntityType.COLLI}
            sortingColumns={['label', 'creation_timestamp']}
            columns={{
                columns: ['label', 'type', 'state', 'current_spot_id', 'current_slot_id', 'index'],
                headerOverwrite: new Map([
                    ['label', CommonMessage.OBJECTS.PARCEL.DEFAULT],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE],
                    [
                        'current_spot_id',
                        <FormattedMessage
                            key='pudo.collis.header.current-spot'
                            id='pudo.collis.header.current-spot'
                            defaultMessage='Current SPOT'
                        />
                    ],
                    [
                        'current_slot_id',
                        <FormattedMessage
                            key='pudo.collis.header.current-slot'
                            id='pudo.collis.header.current-slot'
                            defaultMessage='Current SLOT'
                        />
                    ]
                ]),
                customCells: new Map([
                    [
                        'type',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <DefaultBadge
                                    styles={{ container: { color: 'bg-brand-muted' } }}
                                    label={formatColliType(obj.row.original.type)}
                                />
                            </td>
                        )
                    ],
                    [
                        'state',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <ColliStateBadge state={obj.row.original.state} />
                            </td>
                        )
                    ],
                    [
                        'current_spot_id',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    id={obj.row.original.current_spot_id ?? undefined}
                                    entityType={EntityType.SPOT}
                                />
                            </td>
                        )
                    ],
                    [
                        'current_slot_id',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    id={obj.row.original.current_slot_id ?? undefined}
                                    entityType={EntityType.SLOT}
                                    parentIds={buildSlotParentIds({
                                        spotId: obj.row.original.current_spot_id,
                                        moduleId: obj.row.original.current_module_id
                                    })}
                                />
                            </td>
                        )
                    ]
                ])
            }}
        />
    </div>
);

export const TransactionPicturesAndCommentsSection = ({ obj }: { obj: TransactionDetail }) => {
    const attachments = useTransactionAttachments(obj);
    const viewProps = { attachments };
    return (
        <div className='inline-flex w-full flex-col items-start justify-start gap-6 self-stretch'>
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='inline-flex items-center justify-between self-stretch'>
                    <div className='text-foreground-default justify-start text-xl font-semibold'>
                        <FormattedMessage
                            id='Transaction.Attachments.Title'
                            description='Title of the transaction attachment card'
                            defaultMessage='Attachments'
                        />
                    </div>
                </div>
                <div className='inline-flex flex-wrap content-start items-start justify-start gap-3 self-stretch'>
                    {viewProps.attachments.length > 0 ? (
                        viewProps.attachments.map((attachment) =>
                            attachment.type.startsWith('image/') ? (
                                <button
                                    type='button'
                                    onClick={() => window.open(attachment.url, '_blank')}
                                    key={attachment.id}
                                    className='bg-background-default outline-background-default relative size-36 cursor-pointer overflow-hidden rounded-xl outline-1 outline-offset-[-1px] transition-all duration-200 hover:scale-103 hover:shadow-md'
                                    title={attachment.name}
                                    aria-label={attachment.name}>
                                    <img
                                        src={attachment.url}
                                        className='h-full w-full object-cover'
                                        alt={attachment.name}
                                    />
                                    <span className='absolute right-2 bottom-2 left-2'>
                                        <TransactionAttachmentTypeBadge type={attachment.attachmentType} />
                                    </span>
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={() => window.open(attachment.url, '_blank')}
                                    key={attachment.id}
                                    title={attachment.name}
                                    className='bg-background-default outline-background-default text-foreground-default flex size-36 cursor-pointer flex-col items-center justify-center gap-3 rounded-md p-3 text-center outline-1 outline-offset-[-1px] transition-all duration-200 hover:scale-103 hover:shadow-md'>
                                    <LuFile className='text-brand-muted size-8 shrink-0' />
                                    <span className='line-clamp-2 w-full text-sm font-medium'>{attachment.name}</span>
                                    <TransactionAttachmentTypeBadge type={attachment.attachmentType} />
                                </button>
                            )
                        )
                    ) : (
                        <div className='text-foreground-default justify-start text-sm font-normal'>
                            <FormattedMessage
                                id='Transaction.Attachments.None'
                                description='Shown when a transaction has no attachment'
                                defaultMessage='No attachments'
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.COMMENTS}</div>
                {obj.comments && <div className='text-foreground-default justify-start text-sm font-normal'>{obj.comments}</div>}
            </div>
        </div>
    );
};

export const TransactionLabelsSection = ({
    obj,
    viewProps,
    dispatch
}: {
    obj: TransactionDetail;
    viewProps: ReturnType<typeof useTransactionDetailModal>;
    dispatch: ReturnType<typeof useAppDispatch>;
}) => (
    <div className='inline-flex w-full flex-col items-start justify-start gap-6 self-stretch'>
        <ReactiveObjectList<Label>
            key={'labels-list'}
            fetch={(args) => LabelClient.fetchLabels({ ...args, transaction_id: obj.id, account_id: obj.account_id })}
            dispatch={dispatch}
            columns={{
                columns: ['language', 'colli_id', 'width', 'height'],
                headerOverwrite: new Map([
                    ['width', CommonMessage.OBJECTS.DIMENSION.WIDTH],
                    ['height', CommonMessage.OBJECTS.DIMENSION.HEIGHT]
                ]),
                customActionButtons: [
                    {
                        label: (
                            <FormattedMessage
                                id='labels.previewPdf'
                                defaultMessage='Preview PDF'
                            />
                        ),
                        onClick: (obj) => {
                            LabelClient.fetchPdfPreview(obj.account_id, obj.id).then((blob) => {
                                const url = window.URL.createObjectURL(blob);
                                window.open(url, '_blank');
                            });
                        }
                    },
                    {
                        label: (
                            <FormattedMessage
                                id='labels.previewHtml'
                                defaultMessage='Preview HTML'
                            />
                        ),
                        onClick: (obj) => {
                            LabelClient.fetchHtmlPreview(obj.account_id, obj.id).then((blob) => {
                                const url = window.URL.createObjectURL(blob);
                                window.open(url, '_blank');
                            });
                        }
                    },
                    {
                        label: (
                            <FormattedMessage
                                id='labels.printLabel'
                                defaultMessage='Print label'
                            />
                        ),
                        onClick: (obj) =>
                            viewProps.openModal('print-label-modal', () => (
                                <PrintModal
                                    id='print-label-modal'
                                    onCancel={() => viewProps.closeModal('print-label-modal')}
                                    dispatch={dispatch}
                                    label={obj}
                                />
                            ))
                    }
                ]
            }}
            type={EntityType.LABEL}
        />
    </div>
);

export const TransactionHistorySection = ({ obj }: { obj: TransactionDetail }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.HISTORY}</div>
            <TransactionHistory
                key='history'
                transactionId={obj.id}
                showChildren
                queryParams={{
                    level: [EventLevel.INFO, EventLevel.ERROR],
                    type: [EventType.TRANSACTION_HISTORY_LOGGED, EventType.NOTIFICATION_SENT]
                }}
                content={(event) => (
                    <div className='flex flex-col gap-2'>
                        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                        <HistoryEventDetails
                            event={event}
                            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
                        />
                        <PlatformEventReferenceBadges
                            event={event}
                            excludeKeys={['transaction_id', 'device_id', 'iot_id', 'locker_id']}
                        />
                    </div>
                )}
            />
        </div>
    );
};

export const TransactionEventsSection = ({ obj }: { obj: TransactionDetail }) => {
    const { locale } = useLocale();
    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.EVENT_LOGS}</div>
            <TransactionHistory
                key='events'
                transactionId={obj.id}
                showChildren
                queryParams={{ level: [EventLevel.INFO, EventLevel.ERROR] }}
                content={(event) => (
                    <div className='flex flex-col gap-2'>
                        <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                        <HistoryEventDetails
                            event={event}
                            timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale)}
                        />
                        <PlatformEventReferenceBadges
                            event={event}
                            excludeKeys={['transaction_id', 'device_id', 'iot_id', 'locker_id']}
                        />
                    </div>
                )}
            />
        </div>
    );
};
